import { useState } from "react";
import { eliminarRol, guardarRol } from "./cuentasService";
import Modal from "../../shared/components/Modal";
import { AYUDA_SECCION, SECCIONES_ASIGNABLES, etiquetaSeccion } from "../auth/secciones";
import { campo, etiqueta } from "../../shared/estilos";
import { avisar, confirmar } from "../../shared/avisos";

const ROL_VACIO = { nombre: "", descripcion: "", acceso_total: false, administra_usuarios: false, permisos: [] };

// Casilla de un grupo: marcada si están todas, a medias si solo algunas.
function CasillaGrupo({ marcadas, total, onCambiar, etiquetaAccesible }) {
  return (
    <input
      type="checkbox"
      aria-label={etiquetaAccesible}
      checked={marcadas === total}
      ref={(el) => el && (el.indeterminate = marcadas > 0 && marcadas < total)}
      onChange={(e) => onCambiar(e.target.checked)}
      className="w-4 h-4"
    />
  );
}

function EditorRol({ inicial, onGuardado, onCerrar }) {
  const [form, setForm] = useState({ ...ROL_VACIO, ...inicial, permisos: inicial.permisos || [] });
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const marcadas = new Set(form.permisos);

  const alternar = (ids, marcar) => {
    const siguiente = new Set(marcadas);
    ids.forEach((id) => (marcar ? siguiente.add(id) : siguiente.delete(id)));
    setForm({ ...form, permisos: [...siguiente] });
  };

  const guardar = async () => {
    setGuardando(true);
    setError("");
    const datos = {
      nombre: form.nombre, descripcion: form.descripcion, acceso_total: form.acceso_total || form.administra_usuarios,
      administra_usuarios: Boolean(form.administra_usuarios), permisos: form.permisos,
    };
    try {
      await guardarRol(form.id, datos);
      onGuardado();
    } catch (e) {
      setError(e.message || "No se pudo guardar el rol");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal titulo={form.id ? "Editar rol" : "Nuevo rol"} onCerrar={onCerrar} ancho="max-w-2xl">
      <div className="space-y-4">
        <div>
          <label htmlFor="rol-nombre" className={etiqueta}>Nombre *</label>
          <input id="rol-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={campo} placeholder="Ej.: Coordinación infantil" />
        </div>
        <div>
          <label htmlFor="rol-descripcion" className={etiqueta}>Descripción</label>
          <textarea id="rol-descripcion" value={form.descripcion || ""} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={campo} rows={2} placeholder="Para qué es este rol" />
        </div>

        <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-700 cursor-pointer">
          <input type="checkbox" checked={Boolean(form.administra_usuarios)} onChange={(e) => setForm({ ...form, administra_usuarios: e.target.checked })} className="w-4 h-4 mt-0.5" />
          <span>
            <span className="text-white text-sm font-semibold block">Administrador</span>
            <span className="text-gray-400 text-xs">Ve todo y es el único que crea y edita usuarios y roles.</span>
          </span>
        </label>

        {!form.administra_usuarios && (
          <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.acceso_total} onChange={(e) => setForm({ ...form, acceso_total: e.target.checked })} className="w-4 h-4 mt-0.5" />
            <span>
              <span className="text-white text-sm font-semibold block">Acceso total</span>
              <span className="text-gray-400 text-xs">Ve y administra todo, menos usuarios y roles.</span>
            </span>
          </label>
        )}

        {!form.acceso_total && !form.administra_usuarios && (
          <div>
            <p className={etiqueta}>¿Qué puede ver? ({marcadas.size} sección(es) marcada(s))</p>
            <div className="space-y-3">
              {SECCIONES_ASIGNABLES.map(({ grupo, opciones }) => {
                const ids = opciones.map((o) => o.id);
                const enGrupo = ids.filter((id) => marcadas.has(id)).length;
                return (
                  <fieldset key={grupo} className="p-4 rounded-xl border border-gray-700">
                    <legend className="sr-only">{grupo}</legend>
                    <div className="flex items-center gap-3">
                      {opciones.length > 1 && (
                        <CasillaGrupo marcadas={enGrupo} total={ids.length} onCambiar={(marcar) => alternar(ids, marcar)} etiquetaAccesible={`Todo ${grupo}`} />
                      )}
                      <p className="text-white font-bold text-sm">{grupo}</p>
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {opciones.map((o) => (
                        <label key={o.id} className="flex items-start gap-2 cursor-pointer">
                          <input type="checkbox" checked={marcadas.has(o.id)} onChange={(e) => alternar([o.id], e.target.checked)} className="w-4 h-4 mt-0.5" />
                          <span>
                            <span className="text-white text-sm">{o.label}</span>
                            {AYUDA_SECCION[o.id] && <span className="text-gray-500 text-xs block">{AYUDA_SECCION[o.id]}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                );
              })}
            </div>
            <p className="text-gray-500 text-xs mt-2">Configuración solo la ven los roles con acceso total, y Usuarios y Roles solo los administradores.</p>
          </div>
        )}
        {error && <p role="alert" className="text-red-300 text-sm">{error}</p>}
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={guardar} disabled={guardando} className="flex-1 btn-primario">
          {guardando ? "Guardando..." : "Guardar"}
        </button>
        <button onClick={onCerrar} className="flex-1 btn-fantasma">Cancelar</button>
      </div>
    </Modal>
  );
}

// Roles de acceso: cada uno define qué secciones ve su grupo de usuarios.
function RolesConPermisos({ roles, usuarios, onCambio }) {
  const [editando, setEditando] = useState(null);
  const cantidad = (rol) => usuarios.filter((u) => u.rol_id === rol.id).length;

  const eliminar = async (rol) => {
    if (!(await confirmar(`¿Eliminar el rol "${rol.nombre}"?`, { aceptar: "Eliminar" }))) return;
    try {
      await eliminarRol(rol.id);
      onCambio();
    } catch (e) {
      avisar(e.message || "No se pudo eliminar el rol", "error");
    }
  };

  return (
    <div className="panel p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="titulo-panel">Roles</h3>
        <button onClick={() => setEditando(ROL_VACIO)} className="btn-primario btn-sm">
          Agregar
        </button>
      </div>
      <p className="text-gray-400 text-sm mb-4">Cada rol define qué secciones ven sus usuarios. Los cambios se aplican al instante, sin volver a entrar.</p>
      <div className="space-y-3">
        {roles.map((r) => {
          const n = cantidad(r);
          return (
            <div key={r.id} className="panel-interior p-4 transition hover:border-white/30">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-white font-semibold">{r.nombre}</p>
                  {r.descripcion && <p className="text-gray-400 text-xs mt-1">{r.descripcion}</p>}
                  <p className="text-gray-500 text-xs mt-1">{n === 1 ? "1 usuario" : `${n} usuarios`}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setEditando(r)} className="btn-fantasma btn-sm">Editar</button>
                  <button onClick={() => eliminar(r)} title={n ? "Pasa sus usuarios a otro rol antes de eliminarlo" : undefined} className="btn-peligro btn-sm">Eliminar</button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {r.administra_usuarios ? (
                  <span className="insignia border-white bg-white text-black">Administrador</span>
                ) : r.acceso_total ? (
                  <span className="insignia border-white bg-white text-black">Acceso total</span>
                ) : (r.permisos || []).length ? (
                  r.permisos.map((id) => (
                    <span key={id} className="insignia border-white/15 text-gray-300">{etiquetaSeccion(id)}</span>
                  ))
                ) : (
                  <span className="text-xs text-red-300">Sin secciones: sus usuarios no pueden entrar</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {editando && (
        <EditorRol
          inicial={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={() => {
            setEditando(null);
            onCambio();
          }}
        />
      )}
    </div>
  );
}

export default RolesConPermisos;
